import React, { type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AppFrame } from '@components/layout/AppFrame/AppFrame';
import { ADDON_ONBOARDINGS, AddOnOnboardingHost } from '@features/addon-onboarding';
import { AppDictationRuntime } from '@features/dictation/components/AppDictationRuntime';
// Import the front-chat feature and widget
import { FrontChat } from '@features/front-chat';
// Direct file import: the intake-hub barrel would pull the lazy-loaded hub into this chunk.
import { IntakeReminderBannerContainer } from '@features/intake-hub/banner/IntakeReminderBannerContainer';
import { JotSupportHostProvider } from '@features/jot-support/components/placement/JotSupportHost';
import { OnboardingView } from '@features/onboarding';
import { SystemNotificationBanner } from '@features/system-notifications';
import {
  useAuthStatus,
  useCan,
  useGetUserInfoQuery,
  useUser,
  useUserOnboarding,
} from '@features/user';
import { Box, CircularProgress } from '@mui/material';
import { StatsigUserGate } from '@services/statsig';

import { UnauthorizedError } from '../components/errors/UnauthorizedError';
import { EncounterNavigationGuard } from '../components/guards/EncounterNavigationGuard';
import { Nav, PublicNav } from '../components/Nav';

import { LockedOutContent } from './LockedOutContent';
import LoginView from './LoginView';
import { MembershipDeactivatedContent } from './MembershipDeactivatedContent';
import SessionExpiredView from './SessionExpiredView';

const EXCEPTED_ROUTES = [
  '/record',
  '/encounters',
  '/user',
  '/group-invite',
  '/practice/clinicians',
  '/practice/subscriptions',
  '/bundles',
];

const isExceptedRoute = (pathname: string) => {
  return EXCEPTED_ROUTES.some(route => pathname.includes(route));
};

export const RootView = ({ children }: { children: ReactNode }) => {
  const { userState } = useUser();
  const authStatus = useAuthStatus();
  const shouldSkipQuery = authStatus === 'unauthenticated' || authStatus === 'initializing';
  const { isLoading: userQueryLoading } = useGetUserInfoQuery(undefined, {
    skip: shouldSkipQuery,
  });
  const isAdmin = useCan('manage_organization');
  const { onboardingSteps } = useUserOnboarding({ skip: shouldSkipQuery });
  const location = useLocation();
  const isExcepted = isExceptedRoute(location.pathname);

  const userEmail = userState?.userInfo?.email || '';
  const paymentStatus = userState?.paymentStatus || '';

  // Determine which navigation to show
  const shouldShowFullNav = () => {
    // Show full Nav for:
    // - Fully authenticated users
    // - Locked out users (they're authenticated but payment issues)
    // - Excepted routes (special pages that always get full nav)
    return authStatus === 'authenticated' || authStatus === 'locked_out' || isExcepted;
  };

  // Show loading while checking authentication
  // Note: Statsig user sync is handled by StatsigUserGate component
  if (
    authStatus === 'initializing' ||
    authStatus === 'checking' ||
    (!shouldSkipQuery && userQueryLoading)
  ) {
    return (
      <Box
        id="root-container"
        className="rr-mask"
        sx={{
          height: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Route to appropriate view based on auth status
  const renderMainContent = () => {
    switch (authStatus) {
      case 'unauthenticated':
        return <LoginView />;

      case 'unauthorized':
        return <SessionExpiredView />;

      case 'error':
        return (
          <UnauthorizedError message="An authentication error occurred. Please log in again." />
        );

      case 'locked_out':
        // Content rendering logic - StatsigUserGate is handled at higher level
        // LockedOutContent handles feature gate checks after user sync completes
        return (
          <LockedOutContent
            paymentStatus={paymentStatus}
            userEmail={userEmail}
            isExcepted={isExcepted}
          >
            {children}
          </LockedOutContent>
        );

      case 'membership_deactivated':
        return <MembershipDeactivatedContent isAdmin={isAdmin} />;

      case 'authenticated':
        // Check if user needs to complete signup
        if (!onboardingSteps['account_creation_onboarding_completed']) {
          return <OnboardingView />;
        }
        // Wrap with navigation guard to intercept encounter navigation while recording.
        // AddOnOnboardingHost lives here (post first-run) so add-on onboarding only
        // fires after account-creation onboarding is complete.
        return (
          <EncounterNavigationGuard>
            {children}
            <AddOnOnboardingHost entries={ADDON_ONBOARDINGS} />
          </EncounterNavigationGuard>
        );

      default:
        console.log('Unknown auth status:', authStatus);
        return children;
    }
  };

  const hasSynchronizedUser =
    authStatus === 'authenticated' ||
    authStatus === 'locked_out' ||
    authStatus === 'membership_deactivated';
  const frame = (
    <AppFrame
      chrome={
        <>
          <SystemNotificationBanner />
          {authStatus === 'authenticated' &&
            onboardingSteps['account_creation_onboarding_completed'] && (
              <IntakeReminderBannerContainer />
            )}
          {hasSynchronizedUser && shouldShowFullNav() ? <Nav /> : <PublicNav />}
        </>
      }
      overlays={<FrontChat />}
      backgroundColor={
        authStatus === 'authenticated' && !onboardingSteps['account_creation_onboarding_completed']
          ? 'rgba(213, 211, 224, 0.3)'
          : 'transparent'
      }
    >
      {renderMainContent()}
    </AppFrame>
  );

  // Nav and route feature gates wait for user sync together.
  if (hasSynchronizedUser) {
    return (
      <StatsigUserGate>
        <JotSupportHostProvider>
          <AppDictationRuntime>{frame}</AppDictationRuntime>
        </JotSupportHostProvider>
      </StatsigUserGate>
    );
  }

  return frame;
};
